import {
  Autocomplete,
  TextField,
  Chip,
  GlobalStyles,
  Box,
} from "@mui/material";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

interface Option {
  value: string | number;
  label: string;
  color?: string;
}

interface MultiSelectChipProps<T extends FieldValues> {
  name: Path<T>;
  control?: Control<T>;
  options: Option[];
  label: string;
  error?: string;
  onChange?: (value: (string | number)[]) => void;
  onRemove?: (value: string | number) => void;
  value?: (string | number)[];
  autoFocus?: boolean;
  zoomLevel?: number;
  fullWidth?: boolean;
}

export const MultiAutoCompleteSelectChip = <T extends FieldValues>({
  name,
  control,
  options,
  label,
  error,
  onChange,
  onRemove,
  value = [],
  autoFocus,
  zoomLevel,
  fullWidth,
}: MultiSelectChipProps<T>) => {
  const filteredOptions = options.filter(
    (option) => !value.includes(option.value)
  );

  const renderAutocomplete = (field: FieldValues) => (
    <>
      <GlobalStyles
        styles={{
          ".MuiAutocomplete-popper": {
            zoom: `${zoomLevel}%`,
          },
        }}
      />
      <Autocomplete
        {...field}
        multiple
        options={filteredOptions}
        getOptionLabel={(option: Option) => option.label}
        sx={{
          "& .MuiAutocomplete-popper": {
            zoom: zoomLevel ? `${zoomLevel}%` : 1,
            backgroundColor: "red",
          },
        }}
        fullWidth={fullWidth}
        value={options.filter((option) => value.includes(option.value))}
        onChange={(_, newValue: Option[]) => {
          const uniqueValues = Array.from(
            new Set(newValue.map((item) => item.value))
          );
          field.onChange(uniqueValues);
          onChange && onChange(uniqueValues);
        }}
        renderOption={(props, option: Option) => (
          <li
            {...props}
            style={{
              backgroundColor: option.color || "transparent",
              color: "#000",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {option.label}
            </Box>
          </li>
        )}
        renderTags={(value: Option[], getTagProps) =>
          value.map((option, index) => (
            <Chip
              label={option.label}
              {...getTagProps({ index })}
              key={option.value}
              color="primary"
              variant="outlined"
              sx={{
                backgroundColor: option.color || "transparent",
                borderColor: option.color,
                "& .MuiChip-label": {
                  color: "#000",
                },
                "& .MuiChip-deleteIcon": {
                  color: "#000",
                  "&:hover": {
                    color: "#000",
                  },
                },
              }}
              onDelete={() => {
                const valueToRemove = option.value;
                if (onRemove) {
                  onRemove(valueToRemove);
                }

                const updatedValues = value
                  .map((opt) => opt.value)
                  .filter((val) => val !== valueToRemove);
                field.onChange(updatedValues);
                onChange && onChange(updatedValues);
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={!!error}
            helperText={error}
            autoFocus={autoFocus}
            sx={{
              "& .MuiAutocomplete-popper": {
                zoom: `${zoomLevel}%`,
                display: "none",
              },
            }}
          />
        )}
      />
    </>
  );

  if (control) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => renderAutocomplete(field)}
      />
    );
  } else {
    return renderAutocomplete({ value: [], onChange: () => {} });
  }
};
